/**
 * CompanyTable Component
 *
 * Reusable table component for displaying companies with sorting and actions.
 */

import React from 'react';
import { Edit2, Trash2, Users, MoreVertical, CreditCard, Building2, Settings } from 'lucide-react';
import { SubscriptionBadge } from './SubscriptionBadge';
import { StatusBadge } from './StatusBadge';
import type { AdminCompany } from '../../services/adminApi';
import { ENTITY_TYPE_INFO } from '../../services/adminApi';

interface CompanyTableProps {
  companies: AdminCompany[];
  loading?: boolean;
  onEdit?: (company: AdminCompany) => void;
  onDelete?: (company: AdminCompany) => void;
  onViewUsers?: (company: AdminCompany) => void;
  onChangeSubscription?: (company: AdminCompany) => void;
  onManageFeatures?: (company: AdminCompany) => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  emptyMessage?: string;
}

export const CompanyTable: React.FC<CompanyTableProps> = ({
  companies,
  loading = false,
  onEdit,
  onDelete,
  onViewUsers,
  onChangeSubscription,
  onManageFeatures,
  sortBy,
  sortOrder,
  onSort,
  emptyMessage = 'No companies found',
}) => {
  const [openMenu, setOpenMenu] = React.useState<number | null>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenu(null);
      }
    };

    if (openMenu !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [openMenu]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const SortHeader: React.FC<{ column: string; label: string }> = ({ column, label }) => {
    const isActive = sortBy === column;
    return (
      <button
        onClick={() => onSort?.(column)}
        className={`
          flex items-center gap-1 text-xs font-medium uppercase tracking-wider
          ${isActive ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}
          transition-colors
        `}
      >
        {label}
        {isActive && (
          <svg
            className={`w-3 h-3 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        )}
      </button>
    );
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-gray-100 rounded-t-lg mb-1" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-16 bg-gray-50 mb-1 rounded" />
        ))}
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-lg">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-4 py-3 text-left">
              {onSort ? <SortHeader column="id" label="ID" /> : <span className="text-xs font-medium text-gray-500 uppercase">ID</span>}
            </th>
            <th scope="col" className="px-4 py-3 text-left">
              {onSort ? <SortHeader column="name" label="Name" /> : <span className="text-xs font-medium text-gray-500 uppercase">Name</span>}
            </th>
            <th scope="col" className="px-4 py-3 text-left hidden md:table-cell">
              <span className="text-xs font-medium text-gray-500 uppercase">Domain</span>
            </th>
            <th scope="col" className="px-4 py-3 text-left">
              <span className="text-xs font-medium text-gray-500 uppercase">Subscription</span>
            </th>
            <th scope="col" className="px-4 py-3 text-left hidden sm:table-cell">
              {onSort ? <SortHeader column="usersCount" label="Users" /> : <span className="text-xs font-medium text-gray-500 uppercase">Users</span>}
            </th>
            <th scope="col" className="px-4 py-3 text-left hidden lg:table-cell">
              <span className="text-xs font-medium text-gray-500 uppercase">Status</span>
            </th>
            <th scope="col" className="px-4 py-3 text-left hidden lg:table-cell">
              <span className="text-xs font-medium text-gray-500 uppercase">Type</span>
            </th>
            <th scope="col" className="px-4 py-3 text-left hidden xl:table-cell">
              {onSort ? <SortHeader column="createdAt" label="Created" /> : <span className="text-xs font-medium text-gray-500 uppercase">Created</span>}
            </th>
            <th scope="col" className="px-4 py-3 text-right">
              <span className="text-xs font-medium text-gray-500 uppercase">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {companies.map((company) => (
            <tr key={company.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                #{company.id}
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">{company.name}</p>
                    {company.billingEmail && (
                      <p className="text-xs text-gray-500">{company.billingEmail}</p>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 hidden md:table-cell">
                {company.domain || '-'}
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <SubscriptionBadge tier={company.subscriptionTier} size="sm" showIcon />
              </td>
              <td className="px-4 py-3 whitespace-nowrap hidden sm:table-cell">
                <div className="flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-700">
                    {company.usersCount}
                    <span className="text-gray-400"> / {company.maxUsers}</span>
                  </span>
                  {company.usersCount >= company.maxUsers && (
                    <span className="text-xs text-red-500 font-medium">(Full)</span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                <StatusBadge status={company.status} size="sm" />
              </td>
              <td className="px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                {company.entity_type && ENTITY_TYPE_INFO[company.entity_type] ? (
                  <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg ${ENTITY_TYPE_INFO[company.entity_type].bgColor} ${ENTITY_TYPE_INFO[company.entity_type].borderColor} border`}>
                    <span className="text-base">{ENTITY_TYPE_INFO[company.entity_type].icon}</span>
                    <span className={`text-xs font-medium ${ENTITY_TYPE_INFO[company.entity_type].textColor}`}>
                      {ENTITY_TYPE_INFO[company.entity_type].label}
                    </span>
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-100 border border-gray-300">
                    <span className="text-base">🚛</span>
                    <span className="text-xs font-medium text-gray-700">Carrier</span>
                  </div>
                )}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 hidden xl:table-cell">
                {formatDate(company.createdAt)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right">
                <div className="flex items-center justify-end gap-1">
                  {/* Quick action buttons - visible on larger screens */}
                  <div className="hidden sm:flex items-center gap-1">
                    {onViewUsers && (
                      <button
                        onClick={() => onViewUsers(company)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View users"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                    )}
                    {onManageFeatures && (
                      <button
                        onClick={() => onManageFeatures(company)}
                        className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="Manage features"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    )}
                    {onEdit && (
                      <button
                        onClick={() => onEdit(company)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit company"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={() => onDelete(company)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete company"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* More menu - always visible */}
                  <div className="relative" ref={openMenu === company.id ? menuRef : null}>
                    <button
                      onClick={() => setOpenMenu(openMenu === company.id ? null : company.id)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>

                    {openMenu === company.id && (
                      <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                        {onViewUsers && (
                          <button
                            onClick={() => {
                              onViewUsers(company);
                              setOpenMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Users className="w-4 h-4" />
                            View Users
                          </button>
                        )}
                        {onEdit && (
                          <button
                            onClick={() => {
                              onEdit(company);
                              setOpenMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit Company
                          </button>
                        )}
                        {onManageFeatures && (
                          <button
                            onClick={() => {
                              onManageFeatures(company);
                              setOpenMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Settings className="w-4 h-4" />
                            Manage Features
                          </button>
                        )}
                        {onChangeSubscription && (
                          <button
                            onClick={() => {
                              onChangeSubscription(company);
                              setOpenMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          >
                            <CreditCard className="w-4 h-4" />
                            Change Subscription
                          </button>
                        )}
                        {onDelete && (
                          <>
                            <div className="border-t border-gray-100 my-1" />
                            <button
                              onClick={() => {
                                onDelete(company);
                                setOpenMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete Company
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CompanyTable;
